/**
 * @fileoverview Pure helper that derives the left-pane widget activation checklist from a `DashboardConfig`.
 * @module ui-admin/components/DashboardCanvasBuilder/widgetActivationList
 *
 * Responsibilities: - Map `config.widgets` to `WidgetActivationEntry[]` in order, defaulting
 *                     empty titles to the widget type's human label and `enabled` to `true`.
 * Dependencies:     - @firesquirrel/local-db (DashboardConfig, DashboardWidget)
 *                   - ./widgetTypes (WIDGET_TYPES)
 * Architecture:     - Pure synchronous helper; zero side effects.
 * Notes:            - Mirrors `bandActivationList` from `@firesquirrel/document-render` and
 *                     `columnActivationList` from `ReportCanvasBuilder`.
 */

import type { DashboardConfig, DashboardWidget } from '@firesquirrel/local-db';
import { WIDGET_TYPES } from './widgetTypes';

/**
 * @contract WidgetActivationEntry
 * Purpose: One row in the left-pane "Dashboard widgets" activation checklist.
 * Fields:  id, type, label, enabled
 * Rules:   `id` is `widget.id`; `type` is `widget.type`; `label` falls back to default widget type
 *          label when `widget.title` is empty/whitespace; `enabled` is `widget.enabled ?? true`.
 * Sync:    N/A (derived from `DashboardConfig`).
 */
export interface WidgetActivationEntry {
  id: string;
  type: DashboardWidget['type'];
  label: string;
  enabled: boolean;
}

/**
 * @process widgetActivationList
 * Steps:   Map `config.widgets` to `WidgetActivationEntry[]` in order.
 * Inputs:  config — the `DashboardConfig`.
 * Outputs: `WidgetActivationEntry[]`.
 * Failure Handling: Handles missing/empty `widgets` array gracefully (`[]`).
 * Idempotency: REQUIRED — pure function.
 */
export function widgetActivationList(config: DashboardConfig): WidgetActivationEntry[] {
  const widgets = config?.widgets ?? [];
  return widgets.map((w) => {
    const trimmed = (w.title ?? '').trim();
    const fallback = WIDGET_TYPES.find((t) => t.type === w.type)?.label ?? w.type;
    return {
      id: w.id,
      type: w.type,
      label: trimmed.length > 0 ? trimmed : fallback,
      enabled: w.enabled ?? true,
    };
  });
}
